import { useMemo } from "react";
import { useSelector } from "react-redux";
import { PassengerType } from "@b2bportal/core-types";
import { getPassengerType } from "@b2bportal/core-utilities";
import {
  type BookingQuestion,
  BookingQuestionId,
  BookingQuestionRequired,
} from "@b2bportal/rbc-activities-api";
import {
  useCheckoutStateSelector,
  TravelerSelectors,
  ParentState,
} from "@hopper-b2b/checkout";
import { getTravelerAgeBand } from "../utils";
import { ActivitiesShopSelectors } from "~core-activities/features";

// Passport-related booking question IDs
const PASSPORT_QUESTION_IDS: BookingQuestionId[] = [
  BookingQuestionId.PassportExpiry,
  BookingQuestionId.PassportNationality,
  BookingQuestionId.PassportPassportNo,
];

const getMandatoryTravelerQuestions = (
  questions: BookingQuestion[] | undefined
): BookingQuestion[] => {
  return (questions ?? []).filter(
    (q) => q.required === BookingQuestionRequired.Mandatory
  );
};

const hasAllMandatoryQuestionsAnswered = (
  mandatoryQuestions: BookingQuestion[],
  selectedTravelerIds: string[],
  answers: Array<{
    travelerNum: number;
    bookingQuestionId: BookingQuestionId;
    answer: string;
    travelerId?: string;
  }>
): boolean => {
  if (mandatoryQuestions.length === 0) return true;
  if (selectedTravelerIds.length === 0) return false;

  // Check each selected traveler has all mandatory questions answered
  for (const travelerId of selectedTravelerIds) {
    for (const q of mandatoryQuestions) {
      // Filter answers for this specific traveler by travelerId
      const answer = answers.find(
        (a) =>
          a.travelerId === travelerId &&
          a.bookingQuestionId === (q.id as BookingQuestionId)
      );
      if (!answer?.answer || answer.answer.trim() === "") {
        return false;
      }
    }
  }
  return true;
};

export interface ActivitiesTravelerValidation {
  isValid: boolean;
  requiresMultiSelectValidation: boolean;
  hasPassportBookingQuestions: boolean;
  travelerBookingQuestions?: BookingQuestion[];
}

/**
 * Hook to validate traveler selection for activities
 * Returns validation status for whether selected travelers match the shop participant selection + whether all mandatory booking questions have been answered
 */
export const useActivitiesTravelerValidation =
  (): ActivitiesTravelerValidation => {
    const participantCount = useSelector(
      ActivitiesShopSelectors.getParticipantCount
    );
    const selectedParticipants = useSelector(
      ActivitiesShopSelectors.getSelectedParticipants
    );
    const travelerBookingQuestions = useSelector(
      ActivitiesShopSelectors.getTravelerBookingQuestions
    );
    const activityAgeBands = useSelector(
      ActivitiesShopSelectors.getActivityAgeBands
    );
    const selectedTravelers = useCheckoutStateSelector(
      TravelerSelectors.getAllSelectedTravelers
    );
    const travellerBookingQuestionAnswers = useCheckoutStateSelector(
      (state) =>
        state.context[ParentState.activityInformation]
          ?.travelerBookingQuestionAnswers ?? []
    );

    return useMemo(() => {
      // If we're not in an activities context, return a valid default state
      // This prevents validation errors when this hook is called from non-activities product types
      if (!participantCount) {
        return {
          isValid: true,
          requiresMultiSelectValidation: false,
          hasPassportBookingQuestions: false,
          travelerBookingQuestions: undefined,
        };
      }

      const hasTravelerSpecificQuestions = Boolean(
        travelerBookingQuestions?.length
      );
      const requiresMultiSelectValidation =
        participantCount > 1 && hasTravelerSpecificQuestions;

      const hasPassportBookingQuestions =
        travelerBookingQuestions?.some(
          (q) =>
            PASSPORT_QUESTION_IDS.includes(q.id as BookingQuestionId) &&
            q.required === BookingQuestionRequired.Mandatory
        ) === true;

      const hasCorrectCount = selectedTravelers?.length === participantCount;

      const requiredAgeBandCounts: Record<string, number> = {};
      selectedParticipants?.forEach((p) => {
        requiredAgeBandCounts[p.ageBand] =
          (requiredAgeBandCounts[p.ageBand] ?? 0) + p.numberOfTravelers;
      });
      const selectedAgeBandCounts: Record<string, number> = {};
      selectedTravelers?.forEach((traveler) => {
        const ageBand = getTravelerAgeBand(traveler, activityAgeBands ?? []);
        if (ageBand) {
          selectedAgeBandCounts[ageBand] =
            (selectedAgeBandCounts[ageBand] ?? 0) + 1;
        }
      });
      // Check that required bands match selected bands exactly (bidirectional)
      const allAgeBands = new Set([
        ...Object.keys(requiredAgeBandCounts),
        ...Object.keys(selectedAgeBandCounts),
      ]);
      const hasCorrectAgeBands = Array.from(allAgeBands).every(
        (ageBand) =>
          (requiredAgeBandCounts[ageBand] ?? 0) ===
          (selectedAgeBandCounts[ageBand] ?? 0)
      );

      const mandatoryQuestions = getMandatoryTravelerQuestions(
        travelerBookingQuestions
      );
      const selectedTravelerIds = selectedTravelers?.map((t) => t.id) ?? [];
      const hasAllBookingQuestionsAnswered = hasAllMandatoryQuestionsAnswered(
        mandatoryQuestions,
        selectedTravelerIds,
        travellerBookingQuestionAnswers
      );

      if (!requiresMultiSelectValidation) {
        // When there are no traveler-specific booking questions, only one traveler is selected (primary).
        // We only validate:
        // 1. That mandatory booking questions are answered
        // 2. That the selected traveler is an adult (not a child or infant)
        // We do NOT validate age bands against participant counts in this case
        const selectedTraveler = selectedTravelers?.[0];
        const isAdult = selectedTraveler?.dateOfBirth
          ? getPassengerType({ dateOfBirth: selectedTraveler.dateOfBirth }) ===
            PassengerType.Adult
          : false;

        return {
          isValid: hasAllBookingQuestionsAnswered && isAdult,
          requiresMultiSelectValidation: false,
          hasPassportBookingQuestions,
          travelerBookingQuestions,
        };
      }

      return {
        isValid:
          hasCorrectCount &&
          hasCorrectAgeBands &&
          hasAllBookingQuestionsAnswered,
        requiresMultiSelectValidation: true,
        hasPassportBookingQuestions,
        travelerBookingQuestions,
      };
    }, [
      participantCount,
      selectedParticipants,
      selectedTravelers,
      travelerBookingQuestions,
      activityAgeBands,
      travellerBookingQuestionAnswers,
    ]);
  };
